import React, { useEffect, useState, useMemo } from 'react'
import DataTable from 'react-data-table-component'
import { Badge } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import McDataServices from '../../services/McDataServices'
import { Box, Typography } from '@mui/material'

const UserActivities = ({ userName }) => {
  const { t } = useTranslation()
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(false)
  const [totalElements, setTotalElements] = useState(0)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)
  const [sort, setSort] = useState('createdAt,desc')
  const [error, setError] = useState(null)

  const fetchActivities = async () => {
    setLoading(true)
    setError(null)
    try {
      const payload = {
        page,
        size,
        sort,
        username: userName
      }

      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
          delete payload[key]
        }
      })

      const response = await McDataServices.getActivities(payload)
      setActivities(response.content)
      setTotalElements(response.totalElements)
    } catch (err) {
      setError(err?.detail || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (userName) {
      fetchActivities()
    }
  }, [userName, page, size, sort])

  const handlePageChange = page => {
    setPage(page - 1)
  }

  const handlePerRowsChange = (newPerPage, page) => {
    setSize(newPerPage)
  }

  const handleSort = (column, sortDirection) => {
    if (column.sortField) {
      setSort(`${column.sortField},${sortDirection}`)
    }
  }

  const columns = useMemo(() => [
    {
      name: t('common.status'),
      selector: row => row.success,
      sortable: true,
      sortField: 'success',
      width: '100px',
      cell: row => (
        <Badge color={row.success ? 'success' : 'danger'} pill>
          {row.success ? t('common.success') : t('common.failed')}
        </Badge>
      )
    },
    {
      name: t('common.endpoint'),
      selector: row => row.endpoint,
      sortable: true,
      sortField: 'endpoint',
      wrap: true
    },
    {
      name: t('common.method'),
      selector: row => row.method,
      sortable: true,
      sortField: 'method',
      width: '100px'
    },
    {
      name: t('common.responseTime'),
      selector: row => row.responseTime,
      sortable: true,
      sortField: 'responseTime',
      right: true,
      width: '150px'
    },
    {
      name: t('common.ip'),
      selector: row => row.ip,
      sortable: true,
      sortField: 'ip',
      width: '150px'
    }
  ], [t])

  return (
    <Box sx={{ p: 0 }}>
      {error && <Typography color="error" sx={{ p: 2 }}>{error}</Typography>}
      <DataTable
        columns={columns}
        data={activities}
        progressPending={loading}
        progressComponent={<div className="p-4 text-center">{t('common.loading')}</div>}
        pagination
        paginationServer
        paginationTotalRows={totalElements}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        paginationDefaultPage={page + 1}
        paginationPerPage={size}
        sortServer
        onSort={handleSort}
        highlightOnHover
        pointerOnHover
        noDataComponent={<div className="p-4 text-center">{t('common.noData') || 'No activities found'}</div>}
        customStyles={{
          table: {
            style: {
              backgroundColor: 'transparent'
            }
          },
          headRow: {
            style: {
              backgroundColor: '#f9fafb',
              borderBottomWidth: '1px',
              borderBottomColor: '#e5e7eb'
            }
          }
        }}
        paginationComponentOptions={{
          rowsPerPageText: t('common.rowsPerPage'),
          rangeSeparatorText: t('common.rangeSeparator')
        }}
      />
    </Box>
  )
}

export default UserActivities
